'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface Breadcrumb {
  label: string;
  href: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  backLabel?: string;
  backHref?: string;
  actions?: React.ReactNode;
  showMobileBack?: boolean;
  hasTabs?: boolean;
}

export default function PageHeader({
  title,
  subtitle,
  breadcrumbs = [],
  backLabel = 'Back',
  backHref,
  actions,
  showMobileBack = true,
  hasTabs = false,
}: PageHeaderProps) {
  const { darkMode } = useDarkMode();
  const router = useRouter();

  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    router.back();
  };

  return (
    <div className="mb-0 sm:mb-6">
      {/* Mobile: Fixed header with Back + Actions */}
      {showMobileBack && (
        <div
          className={`fixed ${hasTabs ? 'top-28' : 'top-16'} right-0 left-0 z-30 px-4 py-3 backdrop-blur-md sm:hidden ${
            darkMode ? 'bg-gray-900/70' : 'bg-blue-50/70'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <nav aria-label="Back" className="shrink-0">
              {backHref ? (
                <Link
                  href={backHref}
                  className={`inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold ${
                    darkMode
                      ? 'bg-gray-800 text-white hover:bg-gray-700'
                      : 'bg-white text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeftIcon aria-hidden="true" className="-ml-0.5 size-5" />
                  {backLabel}
                </Link>
              ) : (
                <button
                  onClick={handleBack}
                  className={`inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold ${
                    darkMode
                      ? 'bg-gray-800 text-white hover:bg-gray-700'
                      : 'bg-white text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeftIcon aria-hidden="true" className="-ml-0.5 size-5" />
                  {backLabel}
                </button>
              )}
            </nav>
            {actions && <div className="flex shrink-0 gap-2 overflow-x-auto">{actions}</div>}
          </div>
        </div>
      )}

      {/* Desktop: Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="hidden sm:flex">
          <ol role="list" className="flex items-center space-x-4">
            {breadcrumbs.map((crumb, index) => (
              <li key={index}>
                <div className="flex items-center">
                  {index > 0 && (
                    <ChevronRightIcon
                      aria-hidden="true"
                      className={`size-5 shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                    />
                  )}
                  <Link
                    href={crumb.href}
                    aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}
                    className={`${index > 0 ? 'ml-4' : ''} text-sm font-medium ${
                      darkMode
                        ? 'text-gray-400 hover:text-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {crumb.label}
                  </Link>
                </div>
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Title and Actions (Desktop only - mobile shows actions in fixed header) */}
      <div
        className={`mt-6 mb-6 ${hasTabs ? 'sm:mt-4' : 'sm:mt-0'} ${showMobileBack ? (hasTabs ? 'pt-14' : 'pt-8') : 'pt-0'} flex items-center justify-between sm:pt-0`}
      >
        <div className="min-w-0 flex-1">
          <h2
            className={`text-2xl/7 font-bold sm:truncate ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            {title}
          </h2>
          {subtitle && (
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className="ml-4 hidden shrink-0 gap-3 sm:flex">{actions}</div>}
      </div>
    </div>
  );
}
